import { Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { Drawer } from 'primeng/drawer';
import { Chip } from 'primeng/chip';
import { firstValueFrom } from 'rxjs';

import {
  AddAppointmentDocumentCommand,
  AppointmentDetailsForAdminResponse,
  AppointmentDocumentDto,
  AppointmentsClient,
  AppointmentStatus,
  DocumentDto,
  FileType,
} from '../../model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../ui/button/button';
import { XClose } from '../../icons/x-close/x-close';
import { SlashCircle01 } from '../../icons/slash-circle-01/slash-circle-01';
import { Calendar } from '../../icons/calendar/calendar';
import { Clock } from '../../icons/clock/clock';
import { Hourglas03 } from '../../icons/hourglas-03/hourglas-03';
import { Pdf } from '../../icons/pdf/pdf';
import { Dwg } from '../../icons/dwg/dwg';
import { LinkExternal02 } from '../../icons/link-external-02/link-external-02';
import { AlertTriangle } from '../../icons/alert-triangle/alert-triangle';
import { InfoCircle } from '../../icons/info-circle/info-circle';
import { Edit03 } from '../../icons/edit-03/edit-03';
import {
  getDateFormatedSrLong,
  getDateFormatedSrWithTime,
} from '../../services/date.service';
import { ClientAppointmentRescheduleDialog } from '../client-appointment-reschedule-dialog/client-appointment-reschedule-dialog';
import { ClientAppointmentCancelDialog } from '../client-appointment-cancel-dialog/client-appointment-cancel-dialog';
import { LoaderService } from '../loader/loader.service';
import { AlertService } from '../../services/alert.service';
import { FileService } from '../../services/file.service';

type DetailsState = 'scheduled' | 'completed' | 'cancelled' | 'noShow';
type FileKind = 'pdf' | 'autoCad';

@Component({
  standalone: true,
  selector: 'app-client-appointment-details-drawer',
  imports: [
    Drawer,
    Chip,
    ButtonComponent,
    XClose,
    SlashCircle01,
    Calendar,
    Clock,
    Hourglas03,
    Pdf,
    Dwg,
    LinkExternal02,
    AlertTriangle,
    InfoCircle,
    Edit03,
    ClientAppointmentRescheduleDialog,
    ClientAppointmentCancelDialog,
  ],
  templateUrl: './client-appointment-details-drawer.html',
  styleUrl: './client-appointment-details-drawer.scss',
})
export class ClientAppointmentDetailsDrawer {
  private readonly _client = inject(AppointmentsClient);
  private readonly _loader = inject(LoaderService);
  private readonly _alert = inject(AlertService);
  private readonly _files = inject(FileService);

  visible = model<boolean>(false);
  appointmentId = input<string | undefined>(undefined);

  cancelled = output<string>();
  rescheduled = output<string>();
  documentUploaded = output<void>();

  details = signal<AppointmentDetailsForAdminResponse | undefined>(undefined);
  isLoading = signal(false);

  showRescheduleDialog = signal(false);
  showCancelDialog = signal(false);

  pdfDoc = computed<DocumentDto | undefined>(() =>
    this.details()?.documents?.find((d) => d.type === FileType.PDF),
  );
  autoCadDoc = computed<DocumentDto | undefined>(() =>
    this.details()?.documents?.find((d) => d.type === FileType.DWG),
  );

  state = computed<DetailsState>(() => {
    const d = this.details();
    if (!d) return 'scheduled';
    if (d.status?.value === AppointmentStatus.CANCELED) return 'cancelled';
    if (d.status?.value === AppointmentStatus.NO_SHOW) return 'noShow';
    if (d.to && d.to.getTime() < Date.now()) return 'completed';
    return 'scheduled';
  });

  isFileMissing = computed<boolean>(() => {
    return this.state() === 'scheduled' && (!this.pdfDoc() || !this.autoCadDoc());
  });

  canDeleteFile = computed<boolean>(() => {
    return this.state() === 'scheduled' && !this.details()?.isShortNotice;
  });

  constructor() {
    effect(() => {
      const id = this.appointmentId();
      const isOpen = this.visible();
      if (isOpen && id) {
        this._load(id);
      } else if (!isOpen) {
        this.details.set(undefined);
      }
    });
  }

  close() {
    this.visible.set(false);
  }

  statusChipClass(): string {
    const s = this.state();
    if (s === 'cancelled') return 'red sm';
    if (s === 'noShow') return 'warn sm';
    if (s === 'completed') return 'gray sm';
    return 'info sm';
  }

  statusLabel(): string {
    const s = this.state();
    if (s === 'cancelled') return 'Otkazan';
    if (s === 'noShow') return 'Nije se pojavio';
    if (s === 'completed') return 'Završen';
    return this.details()?.status?.description ?? 'Zakazan';
  }

  formatTimeRange(from?: Date, to?: Date): string {
    if (!from || !to) return '';
    const fmt = (d: Date) =>
      d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${fmt(from)} - ${fmt(to)}`;
  }

  formatFileSize(size?: number): string {
    if (!size) return '0 KB';
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  async onFileSelected(event: Event, kind: FileKind) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (file) await this._uploadDocument(file, kind);
  }

  private async _uploadDocument(file: File, kind: FileKind) {
    const appointmentId = this.details()?.id;
    if (!appointmentId) return;

    const type = kind === 'pdf' ? FileType.PDF : FileType.DWG;

    this._loader.setLoadingText = 'Otpremanje fajla...';
    this._loader.setShowLoader = true;
    try {
      const urlRes = await firstValueFrom(
        this._client.getAppointmentDocumentUploadUrl(type),
      );
      const { storageFileName, uploadUrl, contentType } = urlRes.result!;

      try {
        await this._files.uploadFileToS3(uploadUrl!, file, contentType!);
      } catch {
        // S3 errors aren't surfaced by the global error interceptor.
        this._alert.addFailedMsg('Otpremanje fajla nije uspelo.');
        return;
      }

      const command = new AddAppointmentDocumentCommand({
        appointmentId,
        document: new AppointmentDocumentDto({
          name: file.name,
          storageFileName: storageFileName!,
          type,
          size: file.size,
        }),
      });
      const res = await firstValueFrom(this._client.addAppointmentDocument(command));
      this._alert.addSuccessMsg(res.result);
      await this._load(appointmentId);
      this.documentUploaded.emit();
    } catch {
      // getUploadUrl / addAppointmentDocument errors are surfaced by the global error interceptor.
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  deleteDocument(doc: DocumentDto) {
    const appointmentId = this.details()?.id;
    if (!doc.id || !appointmentId) return;
    this._loader.setLoadingText = 'Brisanje fajla...';
    this._loader.setShowLoader = true;
    firstValueFrom(this._client.deleteAppointmentDocument(doc.id)).then((res) => {
      this._alert.addSuccessMsg(res.result);
      this._load(appointmentId);
    });
  }

  handleCancel() {
    if (this.details()?.id) this.showCancelDialog.set(true);
  }

  handleEdit() {
    if (this.details()?.id) this.showRescheduleDialog.set(true);
  }

  onCancelled(id: string) {
    this.cancelled.emit(id);
    this._load(id);
  }

  onRescheduled(id: string) {
    this.rescheduled.emit(id);
    this._load(id);
  }

  private async _load(id: string) {
    this.isLoading.set(true);
    try {
      const res = await firstValueFrom(this._client.getAppointmentDetailsForAdmin(id));
      this.details.set(res.result);
    } finally {
      this.isLoading.set(false);
    }
  }

  protected readonly ButtonSize = ButtonSize;
  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly getDateFormatedSrLong = getDateFormatedSrLong;
  protected readonly getDateFormatedSrWithTime = getDateFormatedSrWithTime;
}
